import React, { useState, useEffect, useMemo } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import PriceChartTileComponent from "../Components/CoTraderPriceChartTileComponent"; // Importing the InfoFlowTile component
import { LineChart, LineChartOptions } from "@seahorse/charts";
import { fetchFilteredPrices } from "../Utils/CoTraderUtils";
import {
  formatChartAxisTime,
  formatTimeWithZone,
  getHighchartsTimeOptions,
  parseUtcTimestampToMs,
} from "../Utils/timezone";
import { useDisplayTimeZone } from "../Utils/displayTimeZone";
import HighchartsReact from "highcharts-react-official";
import Highcharts, { chart } from "highcharts";
const mockGraphData = [
  { timestamp: "Jan 2024", current_price: 140 },
  { timestamp: "Feb 2024", current_price: 145 },
  { timestamp: "Mar 2024", current_price: 138 },
  { timestamp: "Apr 2024", current_price: 150 },
  { timestamp: "May 2024", current_price: 148 },
  { timestamp: "Jun 2024", current_price: 155 },
  { timestamp: "Jul 2024", current_price: 150 },
  { timestamp: "Aug 2024", current_price: 0.0 },
];

const CoTraderPriceWidget = ({}) => {
  // const [currencyPair, setCurrencyPair] = useState("USDJPY");
  const theme = useTheme();
  const { timeZone } = useDisplayTimeZone();
  const colors = {
    dark: theme.globallink.colors.Black[100],
    background: theme.globallink.colors.Grey[1100],
    containerBackground: theme.globallink.colors.DarkGrey[500],
    titleBackground: theme.globallink.colors.White[500],
    textPrimary: theme.globallink.colors.Blue[500],
    textSecondary: theme.globallink.colors.Grey[600],
    border: theme.globallink.colors.Slate[700],
    white: theme.globallink.colors.White[100],
  };
  const [currencyPair, setCurrencyPair] = useState("USDJPY");
  const [chartData, setChartData] = useState(mockGraphData);
  const currentPrice = useMemo(
    () => chartData[chartData.length - 1].current_price,
    [chartData]
  );
  const changeInPrice = useMemo(() => {
    if (chartData.length < 2) return 0; // Handle cases with insufficient data

    const latestPrice = chartData[chartData.length - 1].current_price;
    const previousPrice = chartData[chartData.length - 2].current_price;

    return ((latestPrice - previousPrice) / previousPrice) * 100; // Calculate percentage change
  }, [chartData]);
  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      height: "100%",
      overflow: "hidden",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
    },
    title: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      "@media (max-width: 480px)": {
        display: "none", // Hide chart on small screens
      },
      "@media (max-height: 480px)": {
        display: "none", // Hide chart on small screens
      },
    },
    currencyPair: {
      fontSize: "calc(1.5vw + 10px)", // Dynamic font size
      color: colors.titleBackground,
      "@media (max-width: 768px)": {
        fontSize: "18px", // Adjust for medium screens
      },
      "@media (max-width: 480px)": {
        display: "none", // Hide chart on small screens
      },
    },
    price: {
      fontSize: "calc(1.5vw + 10px)", // Dynamic font size
      color: colors.white,
      
      "@media (max-width: 768px)": {
        fontSize: "18px", // Adjust for medium screens
      },
      "@media (max-width: 480px)": {
        display: "none", // Hide chart on small screens
      },
    },
    change: {
      fontSize: "calc(1vw + 4px)",
      "@media (max-width: 768px)": {
        fontSize: "10px",
      },
      "@media (max-width: 480px)": {
        display: "none", // Hide chart on small screens
      },
    },
    chartWrapper: {
      width: "100%", // Take full available width
 // Optional: Set a max width if needed
      height: "80%", // Adjust height as needed
    },
  });

  const classes = useStyles();
  // Auto-scroll functionality using useEffect and setInterval
  const chartOptions = ChartOptions({
    prices: chartData,
    selectedSymbol: currencyPair,
    timeZone,
  });
  const optionsLine: LineChartOptions = {
    showGradient: true,
    strokeWidth: 2,
    curveType: "linear",
    lineColor: "#A3FF7A", // Light green color for the chart
    hideDots: true,
    isAnimationActive: true,
    xAxis: {
      dataKey: "timestamp",
      tickSize: 2,
      maxTickCount: 6,
      tickLineColor: "#A3FF7A", // Light green color for ticks
      tickLineWidth: 2,
      tickFormatter: (value) => formatTimeWithZone(value, timeZone),
      tick: { fontSize: 9 },
    },
    yAxis: {
      dataKey: "current_price",
      tickLineColor: "#A3FF7A", // Light green color for ticks
      scale: "linear",
      tickLineWidth: 2,
      domain: ["auto"], // Explicitly setting the minimum to a value below the dataset minimum
      tickCount: 5, // Adjust the number of ticks for better spacing
      tick: { fontSize: 9 },
    },
    tooltip: {
      fontSize: 10,
    },
  };
  useEffect(() => {
    // Function to fetch and update chart data
    const updatePrices = () => {
      fetchFilteredPrices(currencyPair, setChartData,()=>{});
    };

    // Fetch immediately when the component is mounted
    updatePrices();

    // Set up the interval to update prices every 20 seconds (20000 ms)
    const intervalId = setInterval(() => {
      updatePrices();
    }, 20000); // 20 seconds

    // Cleanup the interval when the component is unmounted
    return () => clearInterval(intervalId);
  }, [currencyPair]); // Add currencyPair to the dependency array to refetch when it changes

  useEffect(() => {
    let previousValue = localStorage.getItem("EMALPHA_DASHBAR_CURRENCY_PAIR");

    const interval = setInterval(() => {
      const currentValue: string | null = localStorage.getItem(
        "EMALPHA_DASHBAR_CURRENCY_PAIR"
      );

      if (currentValue !== previousValue) {
        setCurrencyPair(currentValue || ""); // Use an empty string as a fallback if currentValue is null
        previousValue = currentValue;
      }
    }, 1000);

    // Cleanup the interval on component unmount
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={classes.container}>
      <div className={classes.title}>
        <div className={classes.currencyPair}>{currencyPair}</div>
        <div className={classes.price}>
          {currentPrice}{" "}
          <div
            className={classes.change}
            style={{ color: changeInPrice >= 0 ? "green" : "red" }}
          >
            {changeInPrice.toFixed(4)}% {changeInPrice >= 0 ? "↑" : "↓"}
          </div>
        </div>
      </div>
      <div className={classes.chartWrapper}>
        {/* Highcharts 11 ignores `time` changes on update; remount instead. */}
        <HighchartsReact
          key={timeZone}
          highcharts={Highcharts}
          options={chartOptions}
        />
      </div>
    </div>
  );
};

export default CoTraderPriceWidget;

export const formattedPrices = (prices: any) => {
  return prices.map((item: any) => {
    return [
      parseUtcTimestampToMs(item.timestamp),
      parseFloat(item.current_price), // Price as a number
    ];
  });
};

export const formattedSpotAsk = (prices: any) => {
  return prices.map((item: any) => [
    parseUtcTimestampToMs(item.timestamp),
    parseFloat(item.spot_ask),
  ]);
};

export const formattedSpotBid = (prices: any) => {
  return prices.map((item: any) => [
    parseUtcTimestampToMs(item.timestamp),
    parseFloat(item.spot_bid),
  ]);
};

export const ChartOptions = ({ prices, selectedSymbol, timeZone }: any) => {
  const theme = useTheme();
  return {
    time: getHighchartsTimeOptions(timeZone),
    chart: {
      type: "line",
      zoomType: "x",
      height: `${window.innerHeight}px`, // Set 30% of window height, minimum 150px,
      reflow: true, // Ensures the chart resizes with the window
      backgroundColor: theme.globallink.colors.Grey[1200],
      marginBottom: 25,  // Adjust the value to reduce space below the x-axis

      
    },
    title: {
      text: selectedSymbol,
      align: "left", // Align the title to the left
      style: {
        color: theme.globallink.colors.Grey[500],
        fontSize: "0.7rem",
      },
      x: 2, // Add a small margin from the left
      y:10, // Adjust the vertical position (default is centered)
    },
    
    xAxis: {
      type: "datetime",
      title: {
        text: "",
      },
      labels: {
        style: {
          color: theme.globallink.colors.Grey[500],
          fontSize: "0.3rem",
        },
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          return formatChartAxisTime(Number(this.value), timeZone);
        },

      },
      zoom: {
        type: "x",
      },
    },
    yAxis: [
      {
        title: {
          text: "",
        },
        opposite: false,
        min:
          prices.length > 0
            ? Math.min(...prices.map((x: any) => x.spot_bid))
            : 0,
        max:
          prices.length > 0
            ? Math.max(...prices.map((x: any) => x.spot_ask))
            : 100,
        gridLineWidth: 0, // Removes grid lines
        plotLines: [
          {
            value: 0, // Line at 0
            color: "white", // Optional: White line
            width: 1, // Thickness
          },
        ],

        style: {
          color: theme.globallink.colors.Grey[500],
        },
        y: 5,  // Adjust this value to move labels closer to the axis
        labels: {
          style: {
            color: theme.globallink.colors.Grey[500],
            fontSize: "0.3rem",
          },
        },
      },
    ],
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      useHTML: true,
      style: {
        color: theme.globallink.colors.Grey[500],
      },

      backgroundColor: theme.globallink.colors.DarkGrey[500],
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const index = this.points ? this.points[0].point.index : 0;
        const bid = parseFloat(prices[index].spot_bid);
        const ask = parseFloat(prices[index].spot_ask);
        const midpoint = (bid + ask) / 2;
        const time = formatTimeWithZone(prices[index].timestamp, timeZone);
        return `
              <b> ${time} </b><br/>
               Spot Bid: <b>${bid.toFixed(4)}</b><br/>
               Spot Mid: <b>${midpoint.toFixed(4)}</b><br/>
               Spot Ask: <b>${ask.toFixed(4)}</b><br/>
            `;
      },
    },
    plotOptions: {
      series: {
        fillOpacity: 0.3,
        marker: {
          enabled: true,
          radius: 1,
        },
      },
    },
    series: [
      {
        type: "area", // Change from "line" to "area" to enable gradient fill
        name: `Spot Price`,
        data: formattedPrices(prices),
        yAxis: 0,
        color: "#a3ff7a", // Green line color
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, "rgba(163, 255, 122, 0.5)"], // Gradient start (semi-transparent #a3ff7a)
            [1, "rgba(163, 255, 122, 0)"], // Gradient end (fully transparent)
          ],
        },
        marker: {
          enabled: true,
          radius: 1,
        },
      },
    ],

    credits: {
      enabled: false,
    },
  };
};
